import React, { useCallback, useEffect, useRef } from 'react';
import { ImageWithFallback } from './ImageWithFallback';
import moment01 from '../../assets/img/our-moment-01.jpg';
import moment02 from '../../assets/img/our-moment-02.jpg';
import moment03 from '../../assets/img/our-moment-03.jpg';
import moment04 from '../../assets/img/our-moment-04.jpg';
import moment05 from '../../assets/img/our-moment-05.jpg';
import moment06 from '../../assets/img/our-moment-06.jpg';
import moment07 from '../../assets/img/our-moment-07.jpg';
import moment08 from '../../assets/img/our-moment-08.jpg';
import moment09 from '../../assets/img/our-moment-09.jpg';
import { type WeddingLanguage, weddingContent } from './content';

interface GalleryProps {
  language: WeddingLanguage;
}

const photos = [
  {
    url: moment01,
    alt: 'Our moment 1',
  },
  {
    url: moment02,
    alt: 'Our moment 2',
  },
  {
    url: moment03,
    alt: 'Our moment 3',
  },
  {
    url: moment04,
    alt: 'Our moment 4',
  },
  {
    url: moment05,
    alt: 'Our moment 5',
  },
  {
    url: moment06,
    alt: 'Our moment 6',
  },
  {
    url: moment07,
    alt: 'Our moment 7',
  },
  {
    url: moment08,
    alt: 'Our moment 8',
  },
  {
    url: moment09,
    alt: 'Our moment 9',
  },
];

const loopingPhotos = [...photos, ...photos, ...photos];

export function Gallery({ language }: GalleryProps) {
  const content = weddingContent[language].gallery;
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const isWrappingRef = useRef(false);
  const isDraggingRef = useRef(false);
  const dragStartXRef = useRef(0);
  const dragStartScrollLeftRef = useRef(0);
  const autoScrollTimeoutRef = useRef<number | null>(null);
  const loopCorrectionTimeoutRef = useRef<number | null>(null);

  const keepScrollLooping = useCallback(() => {
    const scroller = scrollerRef.current;

    if (!scroller || isWrappingRef.current) {
      return;
    }

    const segmentWidth = scroller.scrollWidth / 3;

    if (!segmentWidth) {
      return;
    }

    const jumpWithoutAnimation = (scrollLeft: number) => {
      const previousScrollBehavior = scroller.style.scrollBehavior;

      isWrappingRef.current = true;
      scroller.style.scrollBehavior = 'auto';
      scroller.scrollLeft = scrollLeft;

      window.requestAnimationFrame(() => {
        scroller.style.scrollBehavior = previousScrollBehavior;
        isWrappingRef.current = false;
      });
    };

    if (scroller.scrollLeft < segmentWidth) {
      jumpWithoutAnimation(scroller.scrollLeft + segmentWidth);
    } else if (scroller.scrollLeft >= segmentWidth * 2) {
      jumpWithoutAnimation(scroller.scrollLeft - segmentWidth);
    }
  }, []);

  const scheduleAutoScroll = useCallback(() => {
    const scroller = scrollerRef.current;

    if (!scroller) {
      return;
    }

    if (autoScrollTimeoutRef.current) {
      window.clearTimeout(autoScrollTimeoutRef.current);
    }

    autoScrollTimeoutRef.current = window.setTimeout(() => {
      const firstItem = scroller.querySelector<HTMLElement>('.gallery-item');

      if (!firstItem) {
        return;
      }

      const itemGap = parseFloat(window.getComputedStyle(scroller).columnGap || '0');
      const scrollStep = firstItem.offsetWidth + itemGap;

      scroller.scrollBy({
        left: scrollStep,
        behavior: 'smooth',
      });
    }, 9000);
  }, []);

  const handleGalleryScroll = useCallback(() => {
    keepScrollLooping();

    if (loopCorrectionTimeoutRef.current) {
      window.clearTimeout(loopCorrectionTimeoutRef.current);
    }

    loopCorrectionTimeoutRef.current = window.setTimeout(keepScrollLooping, 700);
    scheduleAutoScroll();
  }, [keepScrollLooping, scheduleAutoScroll]);

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const scroller = scrollerRef.current;

    if (!scroller || event.pointerType === 'touch') {
      return;
    }

    isDraggingRef.current = true;
    dragStartXRef.current = event.clientX;
    dragStartScrollLeftRef.current = scroller.scrollLeft;
    scroller.classList.add('gallery-scroller--dragging');
    scroller.setPointerCapture(event.pointerId);

    if (autoScrollTimeoutRef.current) {
      window.clearTimeout(autoScrollTimeoutRef.current);
    }
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const scroller = scrollerRef.current;

    if (!scroller || !isDraggingRef.current) {
      return;
    }

    event.preventDefault();
    scroller.scrollLeft =
      dragStartScrollLeftRef.current - (event.clientX - dragStartXRef.current);
  };

  const stopDragging = (event: React.PointerEvent<HTMLDivElement>) => {
    const scroller = scrollerRef.current;

    if (!scroller || !isDraggingRef.current) {
      return;
    }

    isDraggingRef.current = false;
    scroller.classList.remove('gallery-scroller--dragging');

    if (scroller.hasPointerCapture(event.pointerId)) {
      scroller.releasePointerCapture(event.pointerId);
    }

    handleGalleryScroll();
  };

  useEffect(() => {
    const scroller = scrollerRef.current;

    if (!scroller) {
      return;
    }

    const setInitialScrollPosition = () => {
      scroller.scrollLeft = scroller.scrollWidth / 3;
    };

    const frameId = window.requestAnimationFrame(setInitialScrollPosition);
    scheduleAutoScroll();

    return () => {
      window.cancelAnimationFrame(frameId);

      if (autoScrollTimeoutRef.current) {
        window.clearTimeout(autoScrollTimeoutRef.current);
      }

      if (loopCorrectionTimeoutRef.current) {
        window.clearTimeout(loopCorrectionTimeoutRef.current);
      }
    };
  }, [scheduleAutoScroll]);

  return (
    <section id="gallery" className="wedding-section wedding-section--white">
      <div className="wedding-container">
        <div className="wedding-header">
          <h2 className="wedding-title">{content.heading}</h2>
          <div className="wedding-divider" />
        </div>

        <div
          className="gallery-scroller"
          ref={scrollerRef}
          onScroll={handleGalleryScroll}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={stopDragging}
          onPointerCancel={stopDragging}
          onPointerLeave={stopDragging}
        >
          {loopingPhotos.map((photo, index) => (
            <div key={index} className="gallery-item">
              <ImageWithFallback src={photo.url} alt={photo.alt} className="gallery-image" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
